import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, DatePicker, Input, Row, Statistic, Table, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import qs from 'qs';
import PageTitle from '@/components/PageTitle';
import BaseSelect from '@/components/Elements/BaseSelect';
import CustomerInfoColumn from '@/components/customers/CustomerInfoColumn';
import { getStaffIncomeDetail } from '@/redux/actions/statisticsActions';
import { cleanObject } from '@/utils/helpers';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

export default function StaffIncomeReport() {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const authUser = useSelector(state => state.auth.authUser);
    const users = useSelector(state => state.config.users);
    const { summary, detail, pagination } = useSelector(state => state.statisticsReducer.staff_income_detail);
    const [loading, setLoading] = useState(false);
    const [keyword, setKeyword] = useState('');
    const canChooseStaff = ['ADMIN', 'KETOAN'].includes(authUser?.role_code);
    const query = qs.parse(location.search.slice(1));

    useEffect(() => {
        const current = qs.parse(location.search.slice(1));
        const next = { ...current };
        if (!next.start_date || !next.end_date) {
            next.start_date = moment().startOf('month').format('YYYY-MM-DD');
            next.end_date = moment().endOf('month').format('YYYY-MM-DD');
        }
        if (!next.user_id && authUser?.id) next.user_id = String(authUser.id);
        if (qs.stringify(current) !== qs.stringify(next)) history.replace({ pathname: location.pathname, search: qs.stringify(next) });
    }, [authUser?.id, history, location.pathname, location.search]);

    const loadReport = useCallback(() => {
        const current = qs.parse(location.search.slice(1));
        setKeyword(String(current.customer_keyword || ''));
        const staffId = canChooseStaff ? current.user_id : authUser?.id;
        if (!staffId || !current.start_date || !current.end_date) return Promise.resolve();
        setLoading(true);
        return dispatch(getStaffIncomeDetail({
            staff_id: staffId, date_from: current.start_date, date_to: current.end_date,
            customer_keyword: current.customer_keyword, page: Number(current.page) || 1, per_page: Number(current.per_page) || 30
        }))
            .finally(() => setLoading(false));
    }, [authUser?.id, canChooseStaff, dispatch, location.search]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    const updateQuery = updates => history.push({ pathname: location.pathname, search: qs.stringify(cleanObject({ ...query, ...updates, page: undefined })) });
    const money = value => Number(value || 0).toLocaleString('vi-VN');
    const SummaryTitle = ({ label, tooltip }) => <span>{label}{' '}
        <Tooltip title={tooltip}><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip>
    </span>;
    const columns = [
        { title: 'Khách hàng', width: 280, render: (_, row) => <CustomerInfoColumn record={{ id: row.customer_id, code: row.customer_code, full_name: row.customer_name }} nameField="full_name" idField="id" codeField="code" /> },
        { title: 'Dịch vụ', dataIndex: 'service_name' },
        { title: 'Nhân viên Sales', dataIndex: 'sales', width: 220, render: sales => (sales || []).map(item => item.name).filter(Boolean).join(', ') || '—' },
        { title: 'Tỷ lệ nhân viên', dataIndex: 'allocation_percent', align: 'right', render: value => `${Number(value || 0).toLocaleString('vi-VN')}%` },
        { title: 'Doanh thu', dataIndex: 'amount', align: 'right', render: value => <strong style={{ color: value < 0 ? '#cf1322' : '#389e0d' }}>{money(value)} đ</strong> },
        { title: 'Ngày thu/hoàn thực tế', dataIndex: 'last_payment_at', align: 'center', render: value => value ? moment(value).format('DD/MM/YYYY HH:mm') : '' },
    ];

    return <div>
        <PageTitle routes={[{ path: '/', breadcrumbName: 'Trang chủ' }, { path: '/staff-income-report', breadcrumbName: 'Doanh thu nhân viên' }]} title="Doanh thu nhân viên" />
        <div className="mt-2">
            <Title level={3}>Báo cáo doanh thu nhân viên</Title>
            <Row gutter={16} style={{ marginBottom: 10 }}><Col xs={24}>
                {canChooseStaff && <BaseSelect options={users} optionValue="user_id" optionLabel="display_name" placeholder="Chọn nhân viên" className="table-button" showSearch style={{ width: 300 }} value={query.user_id ? Number(query.user_id) : undefined} onChange={value => updateQuery({ user_id: value })} />}
                <RangePicker format="DD/MM/YYYY" allowClear={false} className="table-button" value={[query.start_date ? moment(query.start_date) : null, query.end_date ? moment(query.end_date) : null]} onChange={dates => updateQuery({ start_date: dates?.[0]?.format('YYYY-MM-DD'), end_date: dates?.[1]?.format('YYYY-MM-DD') })} />
                <Search className="table-button" style={{ width: 300 }} allowClear enterButton="Lọc" placeholder="Tên, SĐT hoặc mã khách hàng" value={keyword} onChange={event => setKeyword(event.target.value)} onSearch={value => updateQuery({ customer_keyword: value.trim() || undefined })} />
                <Button className="table-button" icon={<ReloadOutlined />} loading={loading} onClick={loadReport}>Refresh</Button>
            </Col></Row>
            {summary && <div className="employee-summary-grid staff-income-summary-grid">
                <Card><Statistic title={<SummaryTitle label="Doanh thu đã phân bổ" tooltip="Tiền khách thực thu theo ngày phiếu nguồn, đã phân bổ cho nhân viên; chỉ trừ khoản hoàn tiền thực tế hoàn tất trong kỳ." />} value={summary.allocated_income} formatter={money} /></Card>
                <Card><Statistic title={<SummaryTitle label="Khách hàng" tooltip="Số khách hàng duy nhất trong các dòng doanh thu được phân bổ cho nhân viên theo bộ lọc hiện tại." />} value={summary.total_customers} /></Card>
                <Card><Statistic title={<SummaryTitle label="Dịch vụ" tooltip="Số dịch vụ duy nhất trong các dòng doanh thu được phân bổ cho nhân viên theo bộ lọc hiện tại; một dịch vụ chỉ được đếm một lần." />} value={summary.total_services} /></Card>
            </div>}
            <Table size="small" bordered rowKey={row => `${row.customer_service_id}:${row.event_type}:${row.allocation_percent}`} columns={columns} dataSource={detail || []} loading={loading}
                summary={() => summary ? <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={4}><strong>Tổng doanh thu toàn kỳ</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right"><strong>{money(summary.allocated_income)} đ</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={5} />
                </Table.Summary.Row> : null}
                scroll={{ x: 'max-content' }} pagination={{
                    current: pagination?.currentPage || 1, pageSize: pagination?.perPage || 30, total: pagination?.total || 0, showSizeChanger: true,
                    onChange: (page, perPage) => history.push({ pathname: location.pathname, search: qs.stringify({ ...query, page, per_page: perPage }) })
                }} />
        </div>
    </div>;
}
